import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import {
  controlKeys,
  jiraIssuesKeys,
  ticketKeys,
} from "../../../../api/queryKeys";
import { Toast } from "../../../../components/Toast";

const useJiraState = (initialIssueDetails, gapID = null) => {
  const [issueSummary, setIssueSummary] = useState(initialIssueDetails.summary);
  const [issueDescription, setIssueDescription] = useState(
    initialIssueDetails.description
  );
  const [selectedProject, setSelectedProject] = useState(
    initialIssueDetails.project
  );
  const [assignee, setAssignee] = useState(initialIssueDetails.assignee);
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [selectedIssueType, setSelectedIssueType] = useState(
    initialIssueDetails.issuetype
  );
  const [labelOptions, setLabelOptions] = useState([]);
  const DEFAULT_LABELS = [];
  const [defaultLabels, setDefaultLabels] = useState(DEFAULT_LABELS);
  const [labels, setLabels] = useState(
    initialIssueDetails.labels || DEFAULT_LABELS
  );
  const [customFieldList, setCustomFieldList] = useState([]);
  const [customFieldsValues, setCustomFieldsValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [includeEntitiesCSV, setIncludeEntitiesCSV] = useState(
    initialIssueDetails.involved_entities
  );
  const [autoTicketingEnabled, setAutoTicketingEnabled] = useState(false);
  const [jiraIssueConfig, setJiraIssueConfig] = useState(null);
  const [loadedFields, setLoadedFields] = useState({
    projects: false,
    assignees: false,
    labels: false,
  });

  const [existingJiraIssue, setExistingJiraIssue] = useState("");
  let linkToGap = "";

  const queryClient = useQueryClient();
  const {
    fetchProjects,
    fetchJiraIssueTypes,
    fetchJiraIssueCustomFields,
    fetchJiraUsers,
    fetchJiraLabels,
    createJiraIssue,
    editJiraIssue,
    fetchJiraIssueConfig,
    fetchJiraDefaultIssueConfig,
    updateJiraIssueConfig,
    updateJiraIssueDefaultConfig,
    deleteJiraIssueConfig,
    linkExistingJiraIssue,
    enableJiraJSM,
    disableJiraJSM,
    fetchJiraIsJSM,
    createJSMIncident,
    trackEvent,
  } = useAPI();

  const { data: isJSM, isLoading: isLoadingIsJSM } = useQuery({
    queryKey: ["jira", "isJSM"],
    queryFn: fetchJiraIsJSM,
    select: (response) => response.data,
  });
  const mutateIsJSM = useMutation({
    mutationFn: async (isOpen) => {
      if (isOpen) {
        await enableJiraJSM();
      } else {
        await disableJiraJSM();
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["jira", "isJSM"]);
    },
  });

  const { data: projectsData, isSuccess: projectsLoadedSuccessfully } =
    useQuery({
      queryKey: ["jira", "projects"],
      queryFn: fetchProjects,
    });

  useEffect(() => {
    if (projectsLoadedSuccessfully) {
      // Handle specific project for customer (if they have this project, its the only one we support for them)
      const cybersecuritySupportProject = projectsData.data.find(
        (project) =>
          project.key === "CYB" && project.name === "Cybersecurity Support"
      );
      if (isJSM && cybersecuritySupportProject) {
        setProjectOptions([cybersecuritySupportProject]);
      } else {
        setProjectOptions(projectsData.data);
      }

      setLoadedFields((prev) => ({ ...prev, projects: true }));
      setLoading(false);
    }
  }, [projectsLoadedSuccessfully, projectsData]);

  const { data: usersData, isSuccess: usersLoadedSuccessfully } = useQuery({
    queryKey: ["jira", "users", selectedProject?.key],
    queryFn: () => fetchJiraUsers(selectedProject.key),
    enabled: !!selectedProject,
  });

  useEffect(() => {
    if (usersLoadedSuccessfully) {
      setAssigneeOptions(usersData.data);
      setLoadedFields((prev) => ({ ...prev, assignees: true }));
    }
  }, [usersLoadedSuccessfully, usersData]);

  const { data: labelsData, isSuccess: labelsLoadedSuccessfully } = useQuery({
    queryKey: ["jira", "labels"],
    queryFn: fetchJiraLabels,
    enabled: !!selectedProject,
  });

  useEffect(() => {
    if (labelsLoadedSuccessfully) {
      setLabelOptions(labelsData.data.values);
      setLoadedFields((prev) => ({ ...prev, labels: true }));
    }
  }, [labelsLoadedSuccessfully, labelsData]);

  const { data: issueTypesData, isSuccess: issueTypesLoadedSuccessfully } =
    useQuery({
      queryKey: ["jira", "issueTypes", selectedProject?.key],
      queryFn: () => fetchJiraIssueTypes(selectedProject.key),
      enabled: !!selectedProject,
    });

  useEffect(() => {
    if (issueTypesLoadedSuccessfully) {
      setIssueTypes(issueTypesData.data);
      setLoading(false);
    }
  }, [issueTypesLoadedSuccessfully, issueTypesData]);

  const { data: customFieldsData, isSuccess: customFieldsLoadedSuccessfully } =
    useQuery({
      queryKey: [
        "jira",
        "customFields",
        selectedProject?.key,
        selectedIssueType?.key,
      ],
      queryFn: () =>
        fetchJiraIssueCustomFields(selectedProject.key, selectedIssueType.key),
      enabled: !!selectedProject && !!selectedIssueType,
    });

  useEffect(() => {
    if (customFieldsLoadedSuccessfully) {
      const custom_fields = Object.values(customFieldsData.data)
        .filter(
          (field) =>
            field[1]?.required && field[1]?.key.startsWith("customfield_")
        )
        .map((field) => field[1]);
      setCustomFieldList(custom_fields);
      setLoading(false);
      setCustomFieldsValues({});
    }
  }, [customFieldsLoadedSuccessfully, customFieldsData]);

  const {
    data: defaultIssueConfig,
    isSuccess: defaultIssueConfigLoadedSuccessfully,
  } = useQuery({
    queryKey: jiraIssuesKeys.jiraDefaultIssueConfig,
    queryFn: fetchJiraDefaultIssueConfig,
  });

  useEffect(() => {
    if (defaultIssueConfigLoadedSuccessfully) {
      if (defaultIssueConfig.data) {
        setDefaultLabels(
          defaultIssueConfig.data.labels?.length > 0
            ? defaultIssueConfig.data.labels
            : DEFAULT_LABELS
        );
      }
    }
  }, [defaultIssueConfigLoadedSuccessfully, defaultIssueConfig]);

  const { data: jiraIssueData, isSuccess: jiraAutoTicketingDetails } = useQuery(
    {
      queryKey: ["jira", "issueConfig"],
      queryFn: fetchJiraIssueConfig,
      enabled: projectOptions.length > 0 && initialIssueDetails != {},
    }
  );

  useEffect(() => {
    if (jiraAutoTicketingDetails && jiraIssueData.data) {
      setJiraIssueConfig(jiraIssueData.data);
      setAutoTicketingEnabled(true);
      setSelectedProject(
        projectOptions.find((p) => p.key === jiraIssueData.data.project)
      );
      setAssignee(jiraIssueData.data.assignee);
      setReporterName(jiraIssueData.data?.reporter);
      setLabels(jiraIssueData.data.labels);
      setCustomFieldsValues(jiraIssueData.data.custom_fields);
      setIncludeEntitiesCSV(jiraIssueData.data.involved_entities);
    }
  }, [jiraAutoTicketingDetails, jiraIssueData, projectOptions]);

  const checkRequiredFields = () => {
    if (!checkRequiredFieldsNoCustom()) {
      return false;
    }
    // check that customfields are filled
    for (const customField of customFieldList) {
      if (customField.required) {
        if (!customFieldsValues[customField.key]) {
          alert(`${customField.name} is required!`);
          return false;
        }
      }
    }

    return true;
  };

  const checkRequiredFieldsNoCustom = () => {
    if (!selectedProject) {
      alert("Project name is required!");
      return false;
    }
    if (!selectedIssueType) {
      alert("Issue type is required!");
      return false;
    }
    if (!issueSummary) {
      alert("Issue summary is required!");
      return false;
    }

    return true;
  };

  const handleCreateIssue = async () => {
    if (!checkRequiredFields()) {
      return;
    }

    let description = issueDescription
      ? issueDescription
      : "No issue description";
    if (gapID) {
      const domain = window.location.href.split("/")[2];
      linkToGap = `${domain}/posture/controls?control_id=${gapID}`;
    }
    let issueContent = {
      summary: issueSummary,
      description: description,
      project: selectedProject.key,
      issue_type: selectedIssueType.name,
      labels: defaultLabels, // Default labels will include default + user chosen labels
      custom_fields: customFieldsValues,
      entity_ids: includeEntitiesCSV ? initialIssueDetails.entity_ids : [],
    };

    if (assignee) {
      issueContent.assignee = assignee;
    }
    createJiraIssue(issueContent, linkToGap).then(
      (res) => {
        Toast.success({
          text: (
            <div>
              Issue created successfully!{" "}
              {
                <a href={res.data.url} target="_blank" rel="noreferrer">
                  Click here to view
                </a>
              }
            </div>
          ),
        });
      },
      (err) => {
        Toast.error(
          "Failed to create an issue, please contact the support team for more information."
        );
      }
    );
    Toast.info("Creating Jira issue...");
    queryClient.invalidateQueries({
      queryKey: ticketKeys.all,
    });
  };

  const handleEditIssue = async () => {
    if (!checkRequiredFieldsNoCustom()) {
      return false;
    }
    let newFields = {};
    let isChanged = false;

    if (selectedIssueType.name !== initialIssueDetails.issuetype?.name) {
      newFields.issuetype = selectedIssueType;
      isChanged = true;
    }
    if (issueSummary !== initialIssueDetails.summary) {
      newFields.summary = issueSummary;
      isChanged = true;
    }
    if (issueDescription !== initialIssueDetails.description) {
      newFields.description = issueDescription;
      isChanged = true;
    }
    if (
      (assignee && initialIssueDetails.assignee === "") ||
      (assignee !== initialIssueDetails.assignee &&
        initialIssueDetails.assignee !== "")
    ) {
      newFields.assignee = assignee;
      if (!assignee) {
        newFields.assignee = null;
      }
      isChanged = true;
    }
    if (labels.join(",") !== initialIssueDetails.labels.join(",")) {
      newFields.labels = labels;
      isChanged = true;
    }
    if (includeEntitiesCSV) {
      newFields.entity_ids = includeEntitiesCSV
        ? initialIssueDetails.entity_ids
        : null;
      isChanged = true;
    }

    if (isChanged) {
      editJiraIssue(gapID, newFields)
        .then(() => {
          Toast.success(`Jira issue saved.`);
          queryClient.invalidateQueries({ queryKey: jiraIssuesKeys.all });
        })
        .catch(() => {
          Toast.error("Failed to save Jira issue.");
        });
    } else {
      Toast.info("No changes detected");
    }

    return true;
  };

  const handleLinkIssue = async () => {
    if (!existingJiraIssue) {
      alert("Jira issue link is required!");
      return false;
    }

    try {
      await linkExistingJiraIssue(existingJiraIssue, gapID);
      queryClient.invalidateQueries({ queryKey: jiraIssuesKeys.all });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      queryClient.invalidateQueries({
        queryKey: controlKeys.controlActivityLogs(gapID),
      });
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const handleSaveIssueConfig = async () => {
    if (!selectedProject) {
      alert("Project name is required!");
      return;
    }
    if (!selectedIssueType) {
      alert("Issue type is required!");
      return;
    }
    // check that customfields are filled
    for (const customField of customFieldList) {
      if (customField.required) {
        if (!customFieldsValues[customField.key]) {
          alert(`${customField.name} is required!`);
          return;
        }
      }
    }

    let issueConfig = {
      project: selectedProject.key,
      issueType: selectedIssueType.name,
      assignee: assignee,
      labels: labels,
      customFields: customFieldsValues,
      involvedEntities: includeEntitiesCSV,
    };

    updateJiraIssueConfig(issueConfig).then(
      (res) => {
        Toast.success("Issue configuration saved successfully!");
      },
      (err) => {
        console.error(err);
        Toast.error(
          "Failed to save issue configuration, please contact the support team for more information."
        );
      }
    );
    Toast.info("Saving Jira issue configuration...");
  };

  const handleSaveDefaultIssueConfig = async () => {
    let issueConfig = {
      labels: defaultLabels,
    };

    updateJiraIssueDefaultConfig(issueConfig).then(
      (res) => {
        Toast.success("Labels saved successfully!");
      },
      (err) => {
        console.error(err);
        Toast.error(
          "Failed to save Labels, please contact the support team for more information."
        );
      }
    );
    Toast.info("Saving Labels...");
  };

  const handleDisableAutoTicketing = async () => {
    Toast.info("Disabling auto ticketing...");
    trackEvent("User clicked on disable Auto Ticketing").then();

    deleteJiraIssueConfig().then(
      (res) => {
        Toast.success("Auto ticketing disabled successfully!");
      },
      (err) => {
        Toast.error(
          "Failed to disable auto ticketing, please contact the support team for more information."
        );
      }
    );
  };

  const handleEnableJSM = async () => {
    Toast.info("Enabling JSM...");
    trackEvent("User clicked on enable JSM").then();

    enableJiraJSM().then(
      (res) => {
        Toast.success("JSM enabled successfully!");
        queryClient.invalidateQueries(["jira", "isJSM"]);
      },
      (err) => {
        Toast.error(
          "Failed to enable JSM, please contact the support team for more information."
        );
      }
    );
  };

  const handleDisableJSM = async () => {
    Toast.info("Disabling JSM...");
    trackEvent("User clicked on disable JSM").then();

    disableJiraJSM().then(
      (res) => {
        Toast.success("JSM disabled successfully!");
        queryClient.invalidateQueries(["jira", "isJSM"]);
      },
      (err) => {
        Toast.error(
          "Failed to disable JSM, please contact the support team for more information."
        );
      }
    );
  };

  // JSM

  // Incident Type is a custom field for a specific customer
  const JSM_INCIDENT_TYPE = {
    PHISHING_ATTEMPT: "Report on a phishing attempt",
    POTENTIAL_VIRUS: "Report on a potential virus issue",
    OTHER: "Other",
  };
  const JSM_INCIDENT_TPYE_ID_BY_NAME = {
    "Report on a phishing attempt": "12662",
    "Report on a potential virus issue": "12663",
    Other: "12664",
  };
  const JSM_PRIORITY = {
    CRITICAL: "Critical",
    HIGHT: "High",
    MEDIUM: "Medium",
    LOW: "Low",
  };
  const [incidentSummary, setIncidentSummary] = useState(
    initialIssueDetails.summary
  );
  const [incidentDescription, setIncidentDescription] = useState(
    initialIssueDetails.description
  );
  const [incidentTypes, setIncidentTypes] = useState(
    Object.values(JSM_INCIDENT_TYPE)
  );
  const [selectedIncidentType, setSelectedIncidentType] = useState(
    initialIssueDetails?.incident_type
  );
  const [incidentPriorities, setIncidentPriorities] = useState(
    Object.values(JSM_PRIORITY)
  );
  const [selectedIncidentPriority, setSelectedIncidentPriority] = useState(
    initialIssueDetails?.priority || JSM_PRIORITY.MEDIUM
  );
  const [reporterName, setReporterName] = useState(
    initialIssueDetails.reporter
  );

  // NOTE: should be specific for customer
  const checkJSMRequiredFields = () => {
    if (!selectedProject) {
      alert("Project name is required!");
      return false;
    }

    if (!incidentSummary) {
      alert("Incident summary is required!");
      return false;
    }
    if (!selectedIncidentType) {
      alert("Incident type is required!");
      return false;
    }
    if (!selectedIncidentPriority) {
      alert("Incident priority is required!");
      return false;
    }

    return true;
  };

  const handleCreateIncident = async () => {
    if (!checkJSMRequiredFields()) {
      return;
    }
    let description = incidentDescription
      ? incidentDescription
      : "No issue description";

    if (gapID) {
      const domain = window.location.href.split("/")[2];
      linkToGap = `${domain}/posture/controls?control_id=${gapID}`;
    }

    let issueContent = {
      summary: incidentSummary,
      description: description,
      incident_type: {
        customfield_11095: {
          id: JSM_INCIDENT_TPYE_ID_BY_NAME[selectedIncidentType],
        },
      },
      priority: { name: selectedIncidentPriority },
    };

    // Set reporter
    const reporter = assigneeOptions.find(
      (assignee) => assignee.displayName === reporterName
    );
    if (reporter) {
      issueContent.reporter = {
        account_id: reporter.accountId,
        display_name: reporter.displayName,
      };
    }

    createJSMIncident(issueContent, linkToGap).then(
      (res) => {
        queryClient.invalidateQueries({ queryKey: ticketKeys.all });
        Toast.success({
          text: (
            <div>
              Incident reported successfully!{" "}
              {
                <a href={res.data.url} target="_blank" rel="noreferrer">
                  Click here to view
                </a>
              }
            </div>
          ),
        });
      },
      (err) => {
        Toast.error(
          "Failed to report an incident, please contact the support team for more information."
        );
      }
    );

    Toast.info("Reporting JSM incident...");
  };

  const handleJSMEditIncident = async () => {
    if (!checkJSMRequiredFields()) {
      return false;
    }
    let newFields = {};
    let isChanged = false;

    if (reporterName !== initialIssueDetails?.reporter) {
      const reporter = assigneeOptions.find(
        (assignee) => assignee.displayName === reporterName
      );
      newFields.reporter = {
        accountId: reporter.accountId,
      };
      isChanged = true;
    }

    if (incidentSummary !== initialIssueDetails.summary) {
      newFields.summary = incidentSummary;
      isChanged = true;
    }
    if (incidentDescription !== initialIssueDetails.description) {
      newFields.description = incidentDescription;
      isChanged = true;
    }

    if (selectedIncidentType !== initialIssueDetails?.incident_type) {
      newFields.customfield_11095 = {
        id: JSM_INCIDENT_TPYE_ID_BY_NAME[selectedIncidentType],
      };
      isChanged = true;
    }

    if (selectedIncidentPriority !== initialIssueDetails?.priority) {
      newFields.priority = { name: selectedIncidentPriority };
      isChanged = true;
    }

    if (isChanged) {
      editJiraIssue(gapID, newFields)
        .then(() => {
          Toast.success(`JSM incident report saved.`);
          queryClient.invalidateQueries({ queryKey: jiraIssuesKeys.all });
          queryClient.invalidateQueries({ queryKey: ticketKeys.all });
        })
        .catch(() => {
          Toast.error("Failed to save JSM incident report.");
        });
    } else {
      Toast.info("No changes detected");
    }

    return true;
  };

  return {
    issueSummary,
    setIssueSummary,
    issueDescription,
    setIssueDescription,
    selectedProject,
    setSelectedProject,
    assignee,
    setAssignee,
    assigneeOptions,
    setAssigneeOptions,
    projectOptions,
    setProjectOptions,
    issueTypes,
    setIssueTypes,
    selectedIssueType,
    setSelectedIssueType,
    labelOptions,
    setLabelOptions,
    labels,
    setLabels,
    customFieldList,
    setCustomFieldList,
    customFieldsValues,
    setCustomFieldsValues,
    loading,
    setLoading,
    includeEntitiesCSV,
    setIncludeEntitiesCSV,
    loadedFields,
    setLoadedFields,
    handleCreateIssue,
    handleEditIssue,
    handleSaveIssueConfig,
    handleDisableAutoTicketing,
    autoTicketingEnabled,
    setAutoTicketingEnabled,
    existingJiraIssue,
    setExistingJiraIssue,
    handleLinkIssue,
    handleSaveDefaultIssueConfig,
    defaultLabels,
    setDefaultLabels,

    //  JSM
    isJSM,
    isLoadingIsJSM,
    mutateIsJSM,
    handleEnableJSM,
    handleDisableJSM,
    incidentSummary,
    setIncidentSummary,
    incidentDescription,
    setIncidentDescription,
    incidentTypes,
    setIncidentTypes,
    selectedIncidentType,
    setSelectedIncidentType,
    incidentPriorities,
    selectedIncidentPriority,
    setSelectedIncidentPriority,
    reporterName,
    setReporterName,
    handleCreateIncident,
    handleJSMEditIncident,
  };
};

export default useJiraState;
